import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { clearAwsImageUrlCache, getAwsImageVariantUrl, normalizeAwsId, resolveImageVariant } from '../service/awsImage';

const AWS_IMAGE_URL_STALE_TIME_MS = 10 * 60 * 1000;
const AWS_IMAGE_URL_CACHE_TIME_MS = 30 * 60 * 1000;

export const awsImageUrlQueryKey = (awsId, variant) => ['aws-image-url', String(awsId), variant];

export const useAwsImageUrl = ({
    awsId,
    aws,
    fallbackUrl,
    variant,
    usage,
    width,
    height
} = {}) => {
    const initialUrl = fallbackUrl ?? aws?.url ?? '';
    const resolvedAwsId = normalizeAwsId(awsId ?? aws?.id);
    const resolvedVariant = useMemo(
        () => variant ?? resolveImageVariant({ usage, width, height }),
        [height, usage, variant, width]
    );

    const query = useQuery(
        resolvedAwsId ? awsImageUrlQueryKey(resolvedAwsId, resolvedVariant) : ['aws-image-url', 'disabled', resolvedVariant],
        () => getAwsImageVariantUrl(resolvedAwsId, resolvedVariant),
        {
            enabled: Boolean(resolvedAwsId),
            staleTime: AWS_IMAGE_URL_STALE_TIME_MS,
            cacheTime: AWS_IMAGE_URL_CACHE_TIME_MS,
            retry: 1,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            keepPreviousData: true
        }
    );

    const shouldUseFallbackUrl = !resolvedAwsId || query.isError;

    return {
        url: query.data?.url ?? (shouldUseFallbackUrl ? initialUrl : ''),
        variant: resolvedVariant,
        isAwsOptimized: Boolean(query.data?.url),
        isLoading: query.isLoading || query.isFetching,
        isError: query.isError,
        canRefresh: Boolean(resolvedAwsId),
        refresh: async () => {
            if (!resolvedAwsId) return undefined;
            clearAwsImageUrlCache(resolvedAwsId, resolvedVariant);
            return query.refetch();
        }
    };
};
